import { Fragment, useContext } from 'react';
import { Trash2, GripVertical } from 'lucide-react';
import { DragGroupContext } from '../ActualVsBudgetTable.jsx';
import { formatCurrencyShort } from '../../lib/format.js';
import MoveButtons from './MoveButtons.jsx';
import TotalCells from './TotalCells.jsx';
import { sumPeriodTotals } from '../../lib/cashflow-totals.js';

/**
 * Ligne "solde intermédiaire" dans le tableau ActualVsBudgetTable.
 *
 * Rendu sur 2 <tr> :
 *   - Row 1 (au-dessus) : libellé "Total" (une seule fois, sticky gauche) +
 *                         les MONTANTS DE SECTION de chaque période
 *                         (= la somme de la section courante, plus petits).
 *   - Row 2 (en dessous): nom du solde (ex. "Marge brute") + les MONTANTS
 *                         CUMUL de chaque période — le vrai solde intermédiaire
 *                         (gros, gras, c'est la valeur principale).
 *
 * Le ref dnd-kit est posé sur la row 2 (data row), qui contient le drag handle.
 *
 * Props :
 *   subtotal      { id, kind, label }
 *   perPeriod     Array<{ period_start, actual_cents, forecast_cents,
 *                         section_actual_cents, section_forecast_cents, ... }>
 *   cols          Array<{ period_start, period_end, is_future, shortLabel }>
 *   editing       boolean
 *   onLabelChange (id, newLabel) => void
 *   onRemove      (id) => void
 *   move          { onMove(delta), isFirst, isLast } — boutons ▲/▼ en édition
 *   onCellClick   (col, mode, perPeriodEntry) => void  — optionnel
 */
export default function SubtotalRow({
  subtotal,
  perPeriod = [],
  cols = [],
  todayISO,
  editing = false,
  onLabelChange,
  onRemove,
  move,
  onCellClick,
}) {
  function getPeriodEntry(col) {
    return perPeriod.find((p) => p.period_start === col.period_start) ?? null;
  }

  const dragCtx = useContext(DragGroupContext);
  const trRef   = dragCtx?.setNodeRef;
  const trStyle = dragCtx?.style;

  const tdBase = 'min-w-[60px] px-1.5 tabular-nums text-right align-middle border-r border-slate-200';
  const clickable = !!onCellClick;

  return (
    <>
      {/* Row 1 — "Total" + montants de SECTION (somme de la section courante) */}
      <tr className="bg-slate-100 border-t-2 border-slate-300">
        <td className="sticky left-0 bg-slate-100 z-10 px-3 pt-1.5 pb-0 border-r border-slate-300">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            Total
          </span>
        </td>
        {cols.map((col) => {
          const pp = getPeriodEntry(col);
          // Affichage HT avec fallback TTC — cohérent avec les catégories au-dessus.
          const secActual   = pp?.section_actual_ht_cents   ?? pp?.section_actual_cents   ?? null;
          const secForecast = pp?.section_forecast_ht_cents ?? pp?.section_forecast_cents ?? null;
          return (
            <Fragment key={col.period_start}>
              {/* Réel section */}
              <td
                className={`${tdBase} pt-1.5 pb-0 text-[10px] font-normal text-slate-500 ${clickable ? 'cursor-pointer hover:bg-slate-200/50' : ''}`}
                onClick={clickable ? () => onCellClick(col, 'actuals', pp) : undefined}
                title="Total de section — Réel"
              >
                {secActual != null && !col.is_future ? formatCurrencyShort(secActual) : '—'}
              </td>
              {/* Prévi section */}
              <td
                className={`${tdBase} pt-1.5 pb-0 text-[10px] italic font-normal text-slate-500 last:border-r-0 ${clickable ? 'cursor-pointer hover:bg-slate-200/50' : ''}`}
                onClick={clickable ? () => onCellClick(col, 'forecasts', pp) : undefined}
                title="Total de section — Prévisionnel"
              >
                {secForecast != null ? formatCurrencyShort(secForecast) : '—'}
              </td>
            </Fragment>
          );
        })}
        {(() => {
          const t = sumPeriodTotals(perPeriod, cols, 'section_');
          return <TotalCells actual={t.actual} forecast={t.forecast} tone="section" />;
        })()}
      </tr>

      {/* Row 2 — nom + montants CUMUL (le vrai solde intermédiaire) */}
      <tr
        ref={trRef}
        style={trStyle}
        {...(dragCtx?.attributes ?? {})}
        className="bg-slate-100 border-b-2 border-slate-300 font-semibold"
      >
        <td className="sticky left-0 bg-slate-100 z-10 px-3 pt-0 pb-1.5 border-r border-slate-300 min-w-[220px]">
          <div className="flex items-center gap-2">
            {editing && dragCtx && (
              <button
                type="button"
                {...dragCtx.listeners}
                aria-label="Glisser pour réordonner"
                className="shrink-0 p-1 -ml-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200 cursor-grab active:cursor-grabbing touch-none"
              >
                <GripVertical size={14} />
              </button>
            )}
            {editing && move && (
              <MoveButtons onMove={move.onMove} isFirst={move.isFirst} isLast={move.isLast} />
            )}
            <div className="flex-1 min-w-0">
              {editing ? (
                <input
                  type="text"
                  value={subtotal.label ?? ''}
                  onChange={(e) => onLabelChange?.(subtotal.id, e.target.value)}
                  aria-label="Nom du solde intermédiaire"
                  className="w-full text-xs font-semibold bg-white border border-slate-300 rounded px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-400 text-slate-800"
                />
              ) : (
                <span className="block text-xs font-semibold text-slate-800 truncate" title={subtotal.label}>
                  {subtotal.label || 'Solde intermédiaire'}
                </span>
              )}
            </div>
            {editing && (
              <button
                type="button"
                onClick={() => onRemove?.(subtotal.id)}
                aria-label="Supprimer ce solde intermédiaire"
                className="shrink-0 p-1 rounded text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
        </td>
        {cols.map((col) => {
          const pp = getPeriodEntry(col);
          // Affichage HT avec fallback TTC — cohérent avec les catégories au-dessus.
          const cumActual   = pp?.actual_ht_cents   ?? pp?.actual_cents   ?? null;
          const cumForecast = pp?.forecast_ht_cents ?? pp?.forecast_cents ?? null;
          return (
            <Fragment key={col.period_start}>
              {/* Réel cumulé — la valeur principale */}
              <td
                className={`${tdBase} pt-0 pb-1.5 text-[11px] font-semibold text-slate-800 ${clickable ? 'cursor-pointer hover:bg-slate-200/50' : ''}`}
                onClick={clickable ? () => onCellClick(col, 'actuals', pp) : undefined}
                title="Solde intermédiaire — Réel cumulé"
              >
                {cumActual != null && !col.is_future ? formatCurrencyShort(cumActual) : '—'}
              </td>
              {/* Prévi cumulé — la valeur principale */}
              <td
                className={`${tdBase} pt-0 pb-1.5 text-[11px] italic font-semibold text-slate-700 last:border-r-0 ${clickable ? 'cursor-pointer hover:bg-slate-200/50' : ''}`}
                onClick={clickable ? () => onCellClick(col, 'forecasts', pp) : undefined}
                title="Solde intermédiaire — Prévisionnel cumulé"
              >
                {cumForecast != null ? formatCurrencyShort(cumForecast) : '—'}
              </td>
            </Fragment>
          );
        })}
        {(() => {
          const t = sumPeriodTotals(perPeriod, cols);
          return <TotalCells actual={t.actual} forecast={t.forecast} tone="cumul" />;
        })()}
      </tr>
    </>
  );
}
